import React from "react";
import { motion } from "framer-motion";
import AuroraLayer from "./hero-layers/AuroraLayer";
import ParticleField from "./hero-layers/ParticleField";
import GridLayer from "./hero-layers/GridLayer";

/**
 * Hero — Apple-inspired cinematic hero section
 * Spatial depth, ethereal motion, confident minimalism
 */

export default function Hero() {
  return (
    <section
      id="hero"
      className="relative flex flex-col items-center justify-center text-center overflow-hidden"
      style={{
        backgroundColor: "#08060F",
        minHeight: "100vh",
        padding: "104px 40px 84px",
      }}
    >
      <style>{`
        @media (max-width: 768px) {
          #hero {
            padding: 76px 24px 72px !important;
          }
        }
        @media (max-width: 1024px) {
          #hero {
            padding: 96px 32px 80px !important;
          }
        }
        @keyframes text-shimmer {
          0% { background-position: 200% center; }
          100% { background-position: -200% center; }
        }
        @keyframes dot-pulse-gold {
          0% { transform: scale(1); opacity: 1; }
          50% { transform: scale(1.6); opacity: 0; }
          100% { transform: scale(1); opacity: 0; }
        }
        @keyframes scroll-dot {
          0% { transform: translateY(0); opacity: 1; }
          100% { transform: translateY(12px); opacity: 0; }
        }
        @keyframes glow-pulse {
          0%, 100% { box-shadow: 0 0 20px rgba(201, 168, 76, 0); }
          50% { box-shadow: 0 0 40px rgba(201, 168, 76, 0.3); }
        }
        @keyframes urgency-bolt {
          0%, 100% { transform: translateY(0) rotate(0deg) scale(1); }
          20% { transform: translateY(-1px) rotate(-10deg) scale(1.08); }
          40% { transform: translateY(0) rotate(10deg) scale(1.08); }
          60% { transform: translateY(-1px) rotate(-6deg) scale(1.04); }
          80% { transform: translateY(0) rotate(6deg) scale(1.04); }
        }
        #hero .hero-urgency-icon {
          display: inline-block;
          animation: urgency-bolt 1.1s ease-in-out infinite;
          filter: drop-shadow(0 0 10px rgba(239, 68, 68, 0.55));
        }
        /* Cross-browser font smoothing fix */
        #hero {
          -webkit-font-smoothing: antialiased;
          -moz-osx-font-smoothing: grayscale;
          text-rendering: optimizeLegibility;
        }
        #hero h1 {
          -webkit-text-size-adjust: 100%;
          font-synthesis: none;
        }
        @media (prefers-reduced-motion: reduce) {
          .aurora-layer-1, .aurora-layer-2, .aurora-layer-3, .aurora-pulse,
          .grid-breathing, .sparkle-node, .hero-urgency-icon {
            animation: none !important;
          }
        }
      `}</style>

      {/* Background Layers — Spatial Depth */}
      <AuroraLayer />
      <ParticleField />
      <GridLayer />

      {/* Content Wrapper */}
      <div className="relative z-10 w-full max-w-[840px] mx-auto flex flex-col items-center">
        
        {/* Premium SaaS Badge */}
        <motion.div
          initial={{ scale: 0.95, opacity: 0 }}
          animate={{ scale: 1, opacity: 1 }}
          transition={{ duration: 0.6, delay: 0.3, ease: [0.22, 1, 0.36, 1] }}
          className="mb-12 inline-flex items-center gap-2 px-4 py-2 rounded-full"
          style={{
            background: "rgba(255,255,255,0.03)",
            border: "1px solid rgba(195, 15, 22, 0.55)",
            boxShadow:
              "0 0 0 1px rgba(195, 15, 22, 0.25), 0 0 22px rgba(195, 15, 22, 0.35)",
            backdropFilter: "blur(12px)",
            WebkitBackdropFilter: "blur(12px)"
          }}
        >
          <span
            className="w-2 h-2 rounded-full"
            style={{
              backgroundColor: "#ff0000",
              boxShadow: "0 0 8px rgba(16, 185, 129, 0.5)"
            }}
          />
          <span
            className="font-syne font-medium text-[11px]"
            style={{
              letterSpacing: "0.1em",
              color: "rgba(255,255,255,0.9)"
            }}
          >
            YOU PAY NOTHING UNTIL WE RECOVER YOUR REVENUE
          </span>
        </motion.div>


        {/* Headline — Premium SaaS Minimalist */}
        <motion.h1
          initial={{ y: 30, opacity: 0 }}
          animate={{ y: 0, opacity: 1 }}
          transition={{ duration: 0.9, delay: 0.5, ease: [0.22, 1, 0.36, 1] }}
          className="w-full font-cormorant font-bold m-0 text-[#F0EBE1] text-[42px] min-[480px]:text-[50px] md:text-[62px] lg:text-[74px] max-w-[900px]"
          style={{ 
            lineHeight: 1.1, 
            letterSpacing: "-0.03em",
          }}
        >
          Your Failed Debit Orders.
          <br />
          <span style={{ 
            backgroundImage: "linear-gradient(90deg, #F0EBE1 0%, #C9A84C 50%, #F0EBE1 100%)",
            backgroundSize: "200% 100%",
            WebkitBackgroundClip: "text",
            WebkitTextFillColor: "transparent",
            backgroundClip: "text",
            animation: "text-shimmer 6s ease-in-out infinite",
          }}>
            Recovered Tonight.
          </span>
        </motion.h1>

        {/* Subheadline — Refined to three punchy lines 
        <motion.p
          initial={{ y: 24, opacity: 0 }}
          animate={{ y: 0, opacity: 1 }}
          transition={{ duration: 0.85, delay: 0.9, ease: [0.22, 1, 0.36, 1] }}
          className="mt-6 max-w-[580px] mx-auto font-syne text-[16px] md:text-[17px] font-normal"
          style={{
            color: "rgba(240, 235, 225, 0.55)",
            lineHeight: 1.7
          }}
        >
          Crown Ledger connects directly to your PayFast,
          <br />
          Peach Payments, or Paystack account and automatically
          <br />
          recovers failed debit orders, expired cards, and lapsed
          <br />
          DebiCheck mandates — via WhatsApp, Email, and SMS.
          <br />
          Timed to South Africa's salary calendar.
        </motion.p> 
        I am might consider using it another time */}

        <motion.p
          initial={{ y: 24, opacity: 0 }}
          animate={{ y: 0, opacity: 1 }}
          transition={{ duration: 0.85, delay: 1.0, ease: [0.22, 1, 0.36, 1] }}
          className="mt-6 max-w-[580px] mx-auto font-syne text-[15px] md:text-[16px] font-medium"
          style={{
            color: "rgba(240, 235, 225, 0.6)",
            lineHeight: 1.6
          }}
        >
          Crown Ledger recovers failed debit orders for South African property agencies, gyms, and subscription businesses via WhatsApp, Email, and DebiCheck re-authentication.
        </motion.p>

        {/* Accent Line */}
        <motion.div
          initial={{ y: 20, opacity: 0 }}
          animate={{ y: 0, opacity: 1 }}
          transition={{ duration: 0.6, delay: 1.2, ease: [0.22, 1, 0.36, 1] }}
          className="mt-4 font-syne text-[14px] md:text-[15px] font-medium"
          style={{
            color: "rgba(201, 168, 76, 0.9)"
          }}
        >
          Zero setup. Zero monthly cost. Pay only when we recover your money.
        </motion.div>

        {/* Premium SaaS CTAs */}
        <div className="mt-12 flex flex-row flex-wrap items-center justify-center gap-3 w-full max-md:flex-col max-md:gap-3 max-md:max-w-[320px]">
          <motion.button
            initial={{ x: 12, opacity: 0 }}
            animate={{ x: 0, opacity: 1 }}
            transition={{ duration: 0.6, delay: 1.1, ease: [0.22, 1, 0.36, 1] }}
            className="group w-full md:w-auto font-syne font-semibold text-[14px] tracking-wide whitespace-nowrap outline-none flex items-center justify-center transition-all duration-300 cursor-pointer"
            style={{
              background: "#FFFFFF",
              color: "#08060F",
              border: "none",
              borderRadius: "12px",
              padding: "18px 40px",
              minHeight: "56px",
              boxShadow: "0 4px 20px rgba(0, 0, 0, 0.1)",
            }}
            onMouseOver={(e) => {
              e.currentTarget.style.transform = "translateY(-2px)";
              e.currentTarget.style.boxShadow = "0 8px 30px rgba(0, 0, 0, 0.15)";
            }}
            onMouseOut={(e) => {
              e.currentTarget.style.transform = "translateY(0)";
              e.currentTarget.style.boxShadow = "0 4px 20px rgba(0, 0, 0, 0.1)";
            }}
          >
            Calculate My Recovery →
          </motion.button>

          <motion.button
            initial={{ x: -12, opacity: 0 }}
            animate={{ x: 0, opacity: 1 }}
            transition={{ duration: 0.6, delay: 1.2, ease: [0.22, 1, 0.36, 1] }}
            className="group w-full md:w-auto font-syne font-medium text-[14px] tracking-wide whitespace-nowrap outline-none flex items-center justify-center transition-all duration-300 cursor-pointer"
            style={{
              background: "transparent",
              color: "rgba(255, 255, 255, 0.8)",
              border: "1px solid rgba(255, 255, 255, 0.2)",
              borderRadius: "12px",
              padding: "18px 40px",
              minHeight: "56px"
            }}
            onMouseOver={(e) => {
              e.currentTarget.style.borderColor = "rgba(255, 255, 255, 0.4)";
              e.currentTarget.style.color = "rgba(255, 255, 255, 1)";
              e.currentTarget.style.background = "rgba(255, 255, 255, 0.05)";
            }}
            onMouseOut={(e) => {
              e.currentTarget.style.borderColor = "rgba(255, 255, 255, 0.2)";
              e.currentTarget.style.color = "rgba(255, 255, 255, 0.8)";
              e.currentTarget.style.background = "transparent";
            }}
          >
            Book a Free Setup Call
          </motion.button>
        </div>

        {/* Urgency Bar */}
        <motion.div
          initial={{ y: 20, opacity: 0 }}
          animate={{ y: 0, opacity: 1 }}
          transition={{ duration: 0.6, delay: 1.6, ease: [0.22, 1, 0.36, 1] }}
          className="mt-8 inline-flex items-center gap-2 px-4 py-2 rounded-full"
          style={{
            background: "rgba(239, 68, 68, 0.1)",
            border: "1px solid rgba(239, 68, 68, 0.3)",
            backdropFilter: "blur(8px)",
            WebkitBackdropFilter: "blur(8px)"
          }}
        >
          <span className="hero-urgency-icon" style={{ color: "#EF4444" }}>
            ⚡
          </span>
          <span
            className="font-syne font-medium text-[11px]"
            style={{
              letterSpacing: "0.05em",
              color: "rgba(255,255,255,0.9)"
            }}
          >
            April 2026: 60-day debit dispute window is live — protect your debit order revenue now
          </span>
        </motion.div>

        {/*motion.div
          initial={{ y: 10, opacity: 0 }}
          animate={{ y: 0, opacity: 1 }}
          transition={{ duration: 0.7, delay: 1.35, ease: [0.22, 1, 0.36, 1] }}
          className="mt-8 max-w-[840px] mx-auto font-syne text-[11px] md:text-[12px] uppercase"
          style={{
            letterSpacing: "0.22em",
            color: "rgba(240, 235, 225, 0.38)",
            lineHeight: 1.7,
          }}
        >
          No monthly fee · PayFast · Peach · Paystack native · DebiCheck re-authentication · POPIA compliant · Response in 24 hours
        </motion.div>*/}



      </div>

      {/* Bottom Scroll Indicator */}
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ duration: 0.6, delay: 1.5 }}
        className="absolute bottom-[36px] left-1/2 -translate-x-1/2 w-[14px] h-[24px] rounded-[10px] flex justify-center pt-1"
        style={{ border: "1px solid rgba(255,255,255,0.15)" }}
      >
        <div
          className="w-[4px] h-[4px] rounded-full"
          style={{
            background: "rgba(201, 168, 76, 0.6)",
            animation: "scroll-dot 1.6s ease-in-out infinite"
          }}
        />
      </motion.div>
    </section>
  );
}
